// Bulk upload on a task with more than one question.
//
// Pages are routed to students, never to questions — a page can hold the end of
// one answer and the start of the next — so the thing that gets assigned to a
// question is the extracted TEXT. The server hands Review the response as
// paragraphs plus a proposed start per question; this module is the state the
// teacher edits (dividers between paragraphs, questions marked not attempted,
// answers typed in) and what is sent back as one answer per question.
//
// Derivation only, no I/O — the same split submissionBatch.js and reviewEdits.js
// keep, so every rule here is testable without a server.
import {
  getParentSerial,
  normalizeQuestionsFromApi,
} from '../../CreateNewTask/utils/questionStructure';

export const STATUS = {
  READY: 'ready',
  CHECK: 'check',
  TYPED: 'typed',
  NOT_ATTEMPTED: 'notAttempted',
  MISSING: 'missing',
};

/**
 * The questions a student actually answers, in task order. A stem with parts is
 * answered at its parts ("1a", "1b"), never at "1". Both API shapes are read: the
 * flat list with parent refs and the nested one with `children`.
 */
export const answerableQuestionsOf = (questions) => {
  const flat = normalizeQuestionsFromApi(questions);
  const isStem = (question) =>
    flat.some(
      (other) =>
        other !== question &&
        String(getParentSerial(other) ?? '') === String(question.serialNumber)
    );
  return flat
    .filter((question) => !isStem(question))
    .map((question) => ({
      serialNumber: String(question.serialNumber),
      question: question.question ?? '',
      parentSerialNumber:
        getParentSerial(question) != null && getParentSerial(question) !== ''
          ? String(getParentSerial(question))
          : null,
      marksOutOf: question.marksOutOf ?? null,
    }));
};

/**
 * The gate on everything in this module: a single-question task never sees the
 * splitter, the endpoints, or a change of behaviour. `questions` absent (older
 * fixtures, a task still loading) reads as single-question for the same reason.
 */
export const isMultiQuestionAssignment = (assignment) =>
  answerableQuestionsOf(assignment?.questions).length > 1;

export const questionLabel = (serialNumber) => `Q${serialNumber}`;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const dividerId = (serialNumber, paragraphIndex) =>
  `${serialNumber}@${paragraphIndex}`;

const byParagraph = (a, b) => a.paragraphIndex - b.paragraphIndex;

/**
 * What Review edits, built from POST .../questionSplit/propose. A proposed start
 * becomes a divider; a question the proposer could not place is simply absent
 * from the dividers, which is what `questionStatuses` reports as missing.
 */
export const splitStateFromProposal = (proposal) => {
  const paragraphs = (proposal?.paragraphs || []).map((paragraph, index) => ({
    index: paragraph?.index ?? index,
    html: paragraph?.html ?? '',
    block: Boolean(paragraph?.block),
  }));
  const questions = (proposal?.questions || []).map((question) => ({
    serialNumber: String(question.serialNumber),
    question: question.question ?? '',
    parentSerialNumber: question.parentSerialNumber ?? null,
    parentQuestion: question.parentQuestion ?? null,
    marksOutOf: question.marksOutOf ?? null,
  }));
  const seen = new Set();
  const dividers = (proposal?.splits || [])
    .filter(
      (split) =>
        split?.serialNumber != null &&
        Number.isInteger(split.startParagraph) &&
        split.startParagraph >= 0 &&
        split.startParagraph < paragraphs.length
    )
    .filter((split) => {
      // One divider per paragraph: a second question claiming the same start is
      // a proposal defect, and the teacher resolves it as a missing question.
      if (seen.has(split.startParagraph)) return false;
      seen.add(split.startParagraph);
      return true;
    })
    .map((split) => ({
      id: dividerId(String(split.serialNumber), split.startParagraph),
      paragraphIndex: split.startParagraph,
      serialNumber: String(split.serialNumber),
      confidence: split.confidence || 'high',
      note: split.note || '',
    }))
    .sort(byParagraph);
  const notes = Object.fromEntries(
    (proposal?.splits || [])
      .filter((split) => split?.serialNumber != null)
      .map((split) => [String(split.serialNumber), split.note || ''])
  );
  return {
    submissionId: proposal?.submissionId ?? null,
    source: proposal?.source ?? null,
    paragraphs,
    questions,
    dividers,
    notAttempted: [...new Set((proposal?.notAttempted || []).map(String))]
      .filter((serial) => questions.some((question) => question.serialNumber === serial)),
    typed: {},
    notes,
    dirty: false,
  };
};

// ---------------------------------------------------------------------------
// Reading the state
// ---------------------------------------------------------------------------

const sortedDividers = (state) => [...(state.dividers || [])].sort(byParagraph);

/**
 * Each divider owns the paragraphs from its own up to the next divider. Whatever
 * sits before the first divider belongs to no question, which `leadingUnassigned`
 * reports so the screen can say so rather than silently dropping it.
 */
export const segmentsOf = (state) => {
  const dividers = sortedDividers(state);
  const paragraphs = state.paragraphs || [];
  const segments = dividers.map((divider, index) => {
    const end = dividers[index + 1]?.paragraphIndex ?? paragraphs.length;
    return {
      dividerId: divider.id,
      serialNumber: divider.serialNumber,
      confidence: divider.confidence,
      paragraphs: paragraphs.slice(divider.paragraphIndex, end),
    };
  });
  return {
    segments,
    leadingUnassigned: dividers.length
      ? paragraphs.slice(0, dividers[0].paragraphIndex)
      : paragraphs,
  };
};

const hasTypedText = (state, serialNumber) =>
  Boolean((state.typed?.[serialNumber] || '').trim());

/** One row per answerable question: what it has, and whether the teacher must act. */
export const questionStatuses = (state) => {
  const { segments } = segmentsOf(state);
  return (state.questions || []).map((question) => {
    const own = segments.filter(
      (segment) => segment.serialNumber === question.serialNumber
    );
    let status = STATUS.MISSING;
    if (own.length) {
      status = own.some((segment) => segment.confidence === 'low')
        ? STATUS.CHECK
        : STATUS.READY;
    } else if (hasTypedText(state, question.serialNumber)) {
      status = STATUS.TYPED;
    } else if ((state.notAttempted || []).includes(question.serialNumber)) {
      status = STATUS.NOT_ATTEMPTED;
    }
    return {
      ...question,
      status,
      paragraphCount: own.reduce(
        (count, segment) => count + segment.paragraphs.length,
        0
      ),
      note: state.notes?.[question.serialNumber] || '',
    };
  });
};

export const isSplitResolved = (state) =>
  Boolean(state) &&
  questionStatuses(state).every((row) => row.status !== STATUS.MISSING);

export const unresolvedQuestionsOf = (state) =>
  questionStatuses(state).filter((row) => row.status === STATUS.MISSING);

// ---------------------------------------------------------------------------
// Editing the state
// ---------------------------------------------------------------------------

const touched = (state, patch) => ({ ...state, ...patch, dirty: true });

const withoutNotAttempted = (state, serialNumber) =>
  (state.notAttempted || []).filter((serial) => serial !== serialNumber);

/** A question that now has text is no longer "not attempted", whichever way it got the text. */
const reconciled = (state) => {
  const { segments } = segmentsOf(state);
  const answered = new Set(segments.map((segment) => segment.serialNumber));
  return {
    ...state,
    notAttempted: (state.notAttempted || []).filter(
      (serial) => !answered.has(serial) && !hasTypedText(state, serial)
    ),
  };
};

/** Replaces any divider already at that paragraph: one start per paragraph. */
export const addDivider = (state, paragraphIndex, serialNumber) => {
  if (
    !Number.isInteger(paragraphIndex) ||
    paragraphIndex < 0 ||
    paragraphIndex >= (state.paragraphs || []).length ||
    !serialNumber
  ) {
    return state;
  }
  const dividers = (state.dividers || []).filter(
    (divider) => divider.paragraphIndex !== paragraphIndex
  );
  dividers.push({
    id: dividerId(serialNumber, paragraphIndex),
    paragraphIndex,
    serialNumber: String(serialNumber),
    confidence: 'high',
    note: 'Placed by you.',
  });
  return reconciled(
    touched(state, {
      dividers: dividers.sort(byParagraph),
      typed: { ...(state.typed || {}), [serialNumber]: '' },
    })
  );
};

export const removeDivider = (state, id) =>
  touched(state, {
    dividers: (state.dividers || []).filter((divider) => divider.id !== id),
  });

/** A step up or down that lands on another divider is refused rather than merged. */
export const moveDivider = (state, id, delta) => {
  const divider = (state.dividers || []).find(
    (candidate) => candidate.id === id
  );
  if (!divider) return state;
  const target = divider.paragraphIndex + delta;
  if (target < 0 || target >= (state.paragraphs || []).length) return state;
  if ((state.dividers || []).some((other) => other.paragraphIndex === target))
    return state;
  return touched(state, {
    dividers: (state.dividers || [])
      .map((other) =>
        other.id === id
          ? {
              ...other,
              id: dividerId(other.serialNumber, target),
              paragraphIndex: target,
              confidence: 'high',
              note: 'Moved by you.',
            }
          : other
      )
      .sort(byParagraph),
  });
};

export const setDividerQuestion = (state, id, serialNumber) =>
  reconciled(
    touched(state, {
      dividers: (state.dividers || []).map((divider) =>
        divider.id === id
          ? {
              ...divider,
              id: dividerId(serialNumber, divider.paragraphIndex),
              serialNumber: String(serialNumber),
              confidence: 'high',
              note: 'Chosen by you.',
            }
          : divider
      ),
      typed: { ...(state.typed || {}), [serialNumber]: '' },
    })
  );

export const markNotAttempted = (state, serialNumber) =>
  touched(state, {
    notAttempted: [...withoutNotAttempted(state, serialNumber), serialNumber],
    typed: { ...(state.typed || {}), [serialNumber]: '' },
  });

export const unmarkNotAttempted = (state, serialNumber) =>
  touched(state, { notAttempted: withoutNotAttempted(state, serialNumber) });

export const setTypedText = (state, serialNumber, text) =>
  reconciled(
    touched(state, {
      typed: { ...(state.typed || {}), [serialNumber]: text ?? '' },
    })
  );

// ---------------------------------------------------------------------------
// What is sent back
// ---------------------------------------------------------------------------

/** Mirrors the server's ParagraphSplitter.join: blocks concatenate, inline runs rejoin with <br>. */
export const joinParagraphs = (paragraphs = []) =>
  paragraphs.reduce((out, paragraph, index) => {
    const next = paragraphs[index + 1];
    const joiner = next && !paragraph.block && !next.block ? '<br>' : '';
    return out + (paragraph.html || '') + joiner;
  }, '');

const escapeHtml = (text) =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Typed answers are plain text; stored the way extraction stores lines. */
export const typedTextToHtml = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(escapeHtml)
    .join('<br>');

/**
 * Request body for PUT /submissions/{id}/questionSplit. A question in two places
 * is sent as two segments and joined server-side, in order.
 */
export const applyPayloadOf = (state) => {
  const { segments } = segmentsOf(state);
  const typedEntries = Object.entries(state.typed || {}).filter(([, text]) =>
    Boolean((text || '').trim())
  );
  return {
    segments: [
      ...segments.map((segment) => ({
        serialNumber: segment.serialNumber,
        text: joinParagraphs(segment.paragraphs),
      })),
      ...typedEntries.map(([serialNumber, text]) => ({
        serialNumber,
        text: typedTextToHtml(text),
      })),
    ],
    notAttempted: [...(state.notAttempted || [])],
  };
};
